/**
 * Estado de sesión de la aplicación.
 *
 * Vive por encima del router (ver `main.tsx`) porque las guardas de ruta lo
 * consultan en `beforeLoad`, antes de que se monte ningún componente. Mientras
 * el estado es `loading` el provider no renderiza la app: así el router nunca
 * ve un estado indeterminado y no hay parpadeo entre `/login` y `/train`.
 *
 * El token de refresco está en una cookie `httpOnly` y el de acceso solo en
 * memoria (ver `api.ts`), así que al arrancar la sesión se recupera con un
 * refresco. Para no pedirlo a ciegas a quien nunca entró, y para poder abrir la
 * app sin red, se guarda en `localStorage` un indicador sin secretos: la cuenta
 * con la que se entró (`SESSION_HINT_KEY`).
 *
 * Los datos locales van por cuenta (ver `offline/account.ts`): la base de la
 * cuenta se abre antes de pintar el árbol privado y se cierra —sin borrar
 * nada— al salir. Cerrar sesión o entrar con otra cuenta espera antes a que
 * la cola de la anterior se detenga, para que nada suyo salga con otro token.
 */
import type { LoginInput, RegisterInput } from '@fitness/core';
import type { AuthResponse, PendingSocialSignup, User } from '@fitness/api-client';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import {
  NetworkError,
  api,
  clearLegacySession,
  clearSessionHint,
  memorySession,
  onSessionChange,
  readSessionHint,
  setSignedOutHandler,
  takeLegacySession,
  writeSessionHint,
} from './api';
import { currentDevice } from './device';
import {
  closeAccount,
  flushAccount,
  forgetCachedProfile,
  openAccount,
  rememberLegacyOwner,
  wipeAccountData,
} from './offline/account';
import { localDbUserId } from './offline/db';
import { queryClient } from './queryClient';
import { Splash } from '@/components/Splash';

/** `loading` sólo existe durante el arranque, mientras se recupera la sesión. */
export type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

export interface AuthContextValue {
  user: User | null;
  status: AuthStatus;
  login: (input: LoginInput) => Promise<User>;
  register: (input: RegisterInput) => Promise<User>;
  logout: () => Promise<void>;
  /** Refresca la copia local del usuario tras editar la cuenta. */
  setUser: (user: User) => void;
  /**
   * Borra la cuenta en el servidor y, si lo acepta, todo lo local: sesión,
   * indicador, caché de consultas y almacén offline. Lanza si el servidor
   * lo rechaza (por ejemplo, `wrong_password`), sin tocar nada.
   */
  deleteAccount: (password: string) => Promise<void>;
  /**
   * Termina el inicio de sesión con Google o Apple al volver del proveedor
   * (el *ticket* va en una cookie). Con una identidad ya vinculada entra por
   * el mismo camino que `login`; con una nueva devuelve el alta pendiente.
   */
  completeSocialLogin: () => Promise<SocialSignInResult>;
  /** Crea la cuenta del alta pendiente (con el código de invitación) y entra. */
  socialSignup: (input: SocialSignupInput) => Promise<User>;
}

/** Resultado de volver del proveedor. */
export type SocialSignInResult =
  { kind: 'signed_in'; user: User } | { kind: 'signup_required'; signup: PendingSocialSignup };

export interface SocialSignupInput {
  displayName: string;
  inviteCode?: string;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Recupera la sesión al arrancar. Devuelve la cuenta con la que seguir, o
 * `null` si no hay sesión.
 *
 * - Si queda la sesión antigua en `localStorage` (con su token de refresco),
 *   el primer refresco la manda en el cuerpo, el servidor contesta con la
 *   cookie y la clave se borra: a partir de ahí todo va por la cookie.
 * - Sin red se sigue con la cuenta del indicador, como antes se seguía con la
 *   sesión guardada: la app abre con sus datos locales y el cliente renueva la
 *   sesión con la cookie en cuanto una petición llega al servidor.
 */
async function restoreSession(): Promise<User | null> {
  const legacy = takeLegacySession();
  const hint = readSessionHint();
  if (legacy === null && hint === null) return null;

  if (legacy !== null) await memorySession.set(legacy);
  try {
    const { user } = await api.auth.refresh();
    clearLegacySession();
    return user;
  } catch (error) {
    if (error instanceof NetworkError) {
      // Sin red. Una sesión antigua sin indicador (el primer arranque tras
      // actualizar) aún puede sacar la cuenta de la caché del service worker.
      if (hint !== null) return hint;
      return api.me.get().catch(() => null);
    }
    // Cookie caducada, revocada o inexistente: se empieza de cero.
    clearLegacySession();
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }): ReactNode {
  const [user, setUserState] = useState<User | null>(null);
  const [status, setStatus] = useState<AuthStatus>('loading');
  // Evita que una carga inicial lenta pise un login que ya ha terminado.
  const bootstrapped = useRef(false);

  useEffect(() => {
    let cancelled = false;

    async function bootstrap(): Promise<void> {
      try {
        // Antes que nada toque la pista de sesión: es la que dice de quién era
        // la base local compartida de antes.
        await rememberLegacyOwner(readSessionHint()?.id ?? null);
        const restored = await restoreSession();
        if (cancelled) return;
        if (restored !== null) {
          await openAccount(restored.id);
          if (cancelled) return;
        }
        setUserState(restored);
        setStatus(restored === null ? 'anonymous' : 'authenticated');
        if (restored !== null) flushAccount();
      } finally {
        bootstrapped.current = true;
      }
    }

    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  // El indicador sigue al estado: se escribe con cada cuenta que entra (o que
  // edita su perfil) y se borra al quedarse sin sesión.
  useEffect(() => {
    if (status === 'authenticated' && user !== null) writeSessionHint(user);
    if (status === 'anonymous') clearSessionHint();
  }, [status, user]);

  // Al quedarse sin sesión se tira la caché de consultas, para no enseñarle a
  // la siguiente cuenta los datos de la anterior. Se hace en un efecto (y no
  // dentro de `logout()`) para que ocurra ya renderizado el árbol sin sesión:
  // vaciar la caché con las pantallas privadas aún montadas dispararía
  // peticiones sin token que el servidor rechazaría con un 401.
  // También se cierra la base local de la cuenta (sin borrarla) y la copia de
  // `/v1/me` del service worker: la sesión puede perderse sin pasar por
  // `logout()` (refresco rechazado).
  useEffect(() => {
    if (status !== 'anonymous') return;
    queryClient.clear();
    void closeAccount();
    void forgetCachedProfile();
  }, [status]);

  // La sesión también puede desaparecer sin pasar por `logout()`: si el
  // refresco es rechazado, el cliente avisa por aquí.
  useEffect(() => {
    setSignedOutHandler(() => {
      setUserState(null);
      setStatus('anonymous');
    });
    const unsubscribe = onSessionChange((session) => {
      if (session === null && bootstrapped.current) {
        setUserState(null);
        setStatus('anonymous');
      }
    });
    return () => {
      setSignedOutHandler(null);
      unsubscribe();
    };
  }, []);

  /**
   * El camino de «sesión iniciada», el mismo para la contraseña y para Google
   * o Apple: lo de la cuenta anterior se detiene antes de que llegue el token
   * nuevo y la base local de la cuenta se abre antes de pintar el árbol
   * privado. `obtain` pide la sesión al servidor; si devuelve `null` (un alta
   * con proveedor que aún no ha terminado) no se entra.
   */
  const signInWith = useCallback(
    async (obtain: () => Promise<AuthResponse | null>): Promise<User | null> => {
      // Lo de la cuenta anterior tiene que estar detenido antes de que llegue
      // el token nuevo.
      await closeAccount();
      const session = await obtain();
      if (session === null) return null;
      const authenticated = session.user;
      bootstrapped.current = true;
      clearLegacySession();
      await openAccount(authenticated.id);
      queryClient.clear();
      setUserState(authenticated);
      setStatus('authenticated');
      flushAccount();
      return authenticated;
    },
    [],
  );

  const login = useCallback(
    async (input: LoginInput): Promise<User> => {
      const authenticated = await signInWith(() =>
        api.auth.login({
          email: input.email,
          password: input.password,
          device: input.device ?? currentDevice(),
        }),
      );
      if (authenticated === null) throw new Error('El inicio de sesión no ha devuelto la cuenta.');
      return authenticated;
    },
    [signInWith],
  );

  const completeSocialLogin = useCallback(async (): Promise<SocialSignInResult> => {
    const outcome: { signup: PendingSocialSignup | null } = { signup: null };
    const authenticated = await signInWith(async () => {
      const done = await api.auth.completeSocial();
      if (done.status === 'signed_in' && done.auth) return done.auth;
      outcome.signup = done.signup ?? null;
      return null;
    });
    if (authenticated !== null) return { kind: 'signed_in', user: authenticated };
    if (outcome.signup === null) {
      throw new Error('La respuesta del servidor no trae ni sesión ni alta.');
    }
    return { kind: 'signup_required', signup: outcome.signup };
  }, [signInWith]);

  const socialSignup = useCallback(
    async (input: SocialSignupInput): Promise<User> => {
      const created = await signInWith(() =>
        api.auth.socialSignup({
          displayName: input.displayName,
          // Un código vacío no se envía, como en el registro.
          ...(input.inviteCode ? { inviteCode: input.inviteCode } : {}),
        }),
      );
      if (created === null) throw new Error('El alta no ha devuelto la cuenta.');
      return created;
    },
    [signInWith],
  );

  const register = useCallback(async (input: RegisterInput): Promise<User> => {
    await closeAccount();
    const { user: created } = await api.auth.register({
      email: input.email,
      password: input.password,
      displayName: input.displayName,
      handle: input.handle,
      // Un código vacío no se envía: el campo es opcional en el formulario.
      ...(input.inviteCode ? { inviteCode: input.inviteCode } : {}),
      device: input.device ?? currentDevice(),
    });
    bootstrapped.current = true;
    clearLegacySession();
    await openAccount(created.id);
    queryClient.clear();
    setUserState(created);
    setStatus('authenticated');
    return created;
  }, []);

  /**
   * Cierra la sesión sin borrar nada del dispositivo: primero se detiene la
   * cola y se cierra la base de la cuenta, y sólo después se revoca la sesión.
   * Lo pendiente se queda en su base y se sube cuando la cuenta vuelva.
   */
  const logout = useCallback(async (): Promise<void> => {
    await closeAccount();
    await api.auth.logout();
    clearLegacySession();
    setUserState(null);
    setStatus('anonymous');
  }, []);

  const deleteAccount = useCallback(async (password: string): Promise<void> => {
    const userId = localDbUserId();
    await api.me.deleteAccount({ password });
    clearLegacySession();
    clearSessionHint();
    if (userId !== null) await wipeAccountData(userId);
    else await closeAccount();
    setUserState(null);
    setStatus('anonymous');
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      status,
      login,
      register,
      logout,
      setUser: setUserState,
      deleteAccount,
      completeSocialLogin,
      socialSignup,
    }),
    [user, status, login, register, logout, deleteAccount, completeSocialLogin, socialSignup],
  );

  return (
    <AuthContext.Provider value={value}>
      {status === 'loading' ? <Splash /> : children}
    </AuthContext.Provider>
  );
}

/** Estado de sesión. Lanza si se usa fuera de `AuthProvider`. */
export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (value === null) throw new Error('useAuth() debe usarse dentro de <AuthProvider>.');
  return value;
}
